import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Stock, StockCreate, StockUpdate } from '../models/Stock';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';

export type StockPagedResult = PagedResult<Stock>;

@Injectable({ providedIn: 'root' })
export class StockService {
  private get baseUrl() { return `${environment.microservices.stock}`; }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // CRUD básico
  /**
   * GET {{stockBase}}/stock?page=&pageSize=&name=
   * O parâmetro `name` é opcional e usa filtro server-side por LIKE.
   */
  getAll(options?: PaginationOptions, name?: string): Observable<StockPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    const trimmedName = (name || '').trim();
    if (trimmedName) params = params.set('name', trimmedName);

    return this.http.get<any>(`${this.baseUrl}/stock`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { 
        this.errorService.showErrorMessage('Erro ao buscar estoques'); 
        return throwError(() => err); 
      })
    );
  }

  getById(id: number): Observable<Stock> {
    return this.http.get<any>(`${this.baseUrl}/stock/${id}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoque'); return throwError(() => err); })
    );
  }

  create(payload: StockCreate): Observable<Stock> {
    const dto = this.mapToDto(payload);
    return this.http.post<any>(`${this.baseUrl}/stock`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao criar estoque'); return throwError(() => err); })
    );
  }

  update(id: number, payload: StockUpdate): Observable<Stock> {
    const dto = this.mapToDto(payload);
    return this.http.put<any>(`${this.baseUrl}/stock/${id}`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao atualizar estoque'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/stock/${id}`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao excluir estoque'); return throwError(() => err); })
    );
  }

  // Consultas específicas
  getByCode(code: string): Observable<Stock | null> {
    return this.http.get<any>(`${this.baseUrl}/stock/code/${encodeURIComponent(code)}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoque por código'); return throwError(() => err); })
    );
  }

  getActive(options?: PaginationOptions): Observable<StockPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock/active`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques ativos'); return throwError(() => err); })
    );
  }

  getByType(type: string, options?: PaginationOptions): Observable<StockPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock/type/${encodeURIComponent(type)}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques por tipo'); return throwError(() => err); })
    );
  }

  getByCategory(category: string, options?: PaginationOptions): Observable<StockPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock/category/${encodeURIComponent(category)}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques por categoria'); return throwError(() => err); })
    );
  }

  searchByName(name: string, options?: PaginationOptions): Observable<StockPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock/name/${encodeURIComponent(name)}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques por nome'); return throwError(() => err); })
    );
  }

  getByWarehouse(warehouseId: number, options?: PaginationOptions): Observable<StockPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock/warehouse/${warehouseId}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques por almoxarifado'); return throwError(() => err); })
    );
  }

  getChildStocks(parentId: number): Observable<Stock[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/${parentId}/children`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar subestoques'); return throwError(() => err); })
    );
  }

  getRootStocks(): Observable<Stock[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/root`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques principais'); return throwError(() => err); })
    );
  }

  // Controle de estoque
  getLowStock(): Observable<Stock[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/low`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques baixos'); return throwError(() => err); })
    );
  }

  getOutOfStock(): Observable<Stock[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/out`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques esgotados'); return throwError(() => err); })
    );
  }

  getOverStock(): Observable<Stock[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/over`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques excedentes'); return throwError(() => err); })
    );
  }

  // Valores financeiros
  getTotalStockValue(): Observable<{ totalValue: number }> {
    return this.http.get<{ totalValue: number }>(`${this.baseUrl}/stock/value/total`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar valor total do estoque'); return throwError(() => err); })
    );
  }

  getTotalStockValueByType(type: string): Observable<{ type: string; totalValue: number }> {
    return this.http.get<{ type: string; totalValue: number }>(`${this.baseUrl}/stock/value/type/${encodeURIComponent(type)}`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar valor do estoque por tipo'); return throwError(() => err); })
    );
  }

  // Gestão de inventário
  updateStockQuantity(id: number, newQuantity: number): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.baseUrl}/stock/${id}/quantity`, { newQuantity }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao atualizar quantidade do estoque'); return throwError(() => err); })
    );
  }



  // Gestão de almoxarifados
  assignToWarehouse(id: number, warehouseId: number, location?: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/stock/${id}/assign-warehouse`, { 
      warehouseId, 
      location 
    }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao atribuir estoque ao almoxarifado'); return throwError(() => err); })
    );
  }

  unassignFromWarehouse(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/stock/${id}/unassign-warehouse`, {}).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao remover estoque do almoxarifado'); return throwError(() => err); })
    );
  }

  transferToWarehouse(id: number, newWarehouseId: number, newLocation?: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/stock/${id}/transfer-warehouse`, { 
      newWarehouseId, 
      newLocation 
    }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao transferir estoque entre almoxarifados'); return throwError(() => err); })
    );
  }

  getUnassignedStocks(): Observable<Stock[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/unassigned`).pipe(
      map(list => this.mapListFromDto(list || [])),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estoques não atribuídos'); return throwError(() => err); })
    );
  }

  getWarehouseOccupationStats(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/stock/warehouse/stats`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar estatísticas de ocupação dos almoxarifados'); return throwError(() => err); })
    );
  }

  // Validações
  checkCodeExists(code: string, excludeId?: number): Observable<{ exists: boolean }> {
    // Se o código está vazio, não precisa verificar
    if (!code || code.trim() === '') {
      return of({ exists: false });
    }

    // Como não existe rota específica para check-code, vamos usar a rota GetStockByCode
    // Se retornar um stock, significa que o código existe
    return this.http.get<any>(`${this.baseUrl}/stock/code/${encodeURIComponent(code.trim())}`).pipe(
      map(stock => {
        // Se encontrou um stock, verificar se é o mesmo que está sendo editado
        if (excludeId && stock && stock.id === excludeId) {
          return { exists: false }; // É o mesmo registro sendo editado
        }
        return { exists: !!stock }; // Se encontrou stock, código existe
      }),
      catchError(err => {
        // Se retornou 404 ou {"error":"Stock not found"}, significa que o código não existe
        if (err.status === 404) {
          return of({ exists: false });
        }
        
        // Verificar se a resposta contém a mensagem "Stock not found"
        if (err.error && err.error.error === "Stock not found") {
          return of({ exists: false });
        }
        
        // Para outros erros, não tratar como "código existe"
        return of({ exists: false });
      })
    );
  }

  // Mapeamentos DTO <-> Front
  private mapFromDto(dto: any): Stock {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);
    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      code: pick(dto, ['Code', 'code']) ?? '',
      name: pick(dto, ['Name', 'name']) ?? '',
      description: pick(dto, ['Description', 'description']),
      parentStockId: pick(dto, ['ParentStockId', 'parentStockId']),
      level: pick(dto, ['Level', 'level']),
      hierarchyPath: pick(dto, ['HierarchyPath', 'hierarchyPath']),
      type: pick(dto, ['Type', 'type']) ?? '',
      category: pick(dto, ['Category', 'category']),
      department: pick(dto, ['Department', 'department']),
      branch: pick(dto, ['Branch', 'branch']),
      warehouseId: pick(dto, ['WarehouseId', 'warehouseId']),
      location: pick(dto, ['Location', 'location']),
      unit: pick(dto, ['Unit', 'unit']),
      minimumQuantity: pick(dto, ['MinimumQuantity', 'minimumQuantity']),
      maximumQuantity: pick(dto, ['MaximumQuantity', 'maximumQuantity']),
      currentQuantity: pick(dto, ['CurrentQuantity', 'currentQuantity']) ?? 0,
      availableQuantity: pick(dto, ['AvailableQuantity', 'availableQuantity']) ?? 0,
      responsiblePerson: pick(dto, ['ResponsiblePerson', 'responsiblePerson']),
      responsibleEmail: pick(dto, ['ResponsibleEmail', 'responsibleEmail']),
      managerStaffId: pick(dto, ['ManagerStaffId', 'managerStaffId']),
      unitCost: pick(dto, ['UnitCost', 'unitCost']),
      totalValue: pick(dto, ['TotalValue', 'totalValue']),
      currencyCode: pick(dto, ['CurrencyCode', 'currencyCode']),
      lastInventoryDate: toIso(pick(dto, ['LastInventoryDate', 'lastInventoryDate'])),
      nextInventoryDate: toIso(pick(dto, ['NextInventoryDate', 'nextInventoryDate'])),
      status: pick(dto, ['Status', 'status']) ?? 'Ativo',
      isActive: pick(dto, ['IsActive', 'isActive']) ?? true,
      allowsNegativeStock: pick(dto, ['AllowsNegativeStock', 'allowsNegativeStock']) ?? false,
      trackBatch: pick(dto, ['TrackBatch', 'trackBatch']) ?? false,
      trackExpiration: pick(dto, ['TrackExpiration', 'trackExpiration']) ?? false,
      stockGroup: pick(dto, ['StockGroup', 'stockGroup']),
      notes: pick(dto, ['Notes', 'notes']),
      createdAt: toIso(pick(dto, ['CreatedAt', 'createdAt'])),
      updatedAt: toIso(pick(dto, ['UpdatedAt', 'updatedAt'])),
      createdBy: pick(dto, ['CreatedBy', 'createdBy']),
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']),
      // Dados relacionados
      parentStockName: pick(dto, ['ParentStockName', 'parentStockName']),
      warehouseName: pick(dto, ['WarehouseName', 'warehouseName']),
      managerStaffName: pick(dto, ['ManagerStaffName', 'managerStaffName']),
      childStocks: pick(dto, ['ChildStocks', 'childStocks']) ? 
        this.mapListFromDto(pick(dto, ['ChildStocks', 'childStocks'])) : []
    };
  }

  private mapListFromDto(list: any[]): Stock[] {
    if (!list || !Array.isArray(list)) return [];
    return list.map(x => this.mapFromDto(x));
  }

  private mapToDto(payload: Partial<StockCreate | StockUpdate>): any {
    return {
      Code: payload.code,
      Name: payload.name,
      Description: payload.description,
      ParentStockId: payload.parentStockId,
      Level: payload.level,
      HierarchyPath: payload.hierarchyPath,
      Type: payload.type,
      Category: payload.category,
      Department: payload.department,
      Branch: payload.branch,
      WarehouseId: payload.warehouseId,
      Location: payload.location,
      Unit: payload.unit,
      MinimumQuantity: payload.minimumQuantity,
      MaximumQuantity: payload.maximumQuantity,
      CurrentQuantity: payload.currentQuantity,
      AvailableQuantity: payload.availableQuantity,
      ResponsiblePerson: payload.responsiblePerson,
      ResponsibleEmail: payload.responsibleEmail,
      ManagerStaffId: payload.managerStaffId,
      UnitCost: payload.unitCost,
      TotalValue: payload.totalValue,
      CurrencyCode: payload.currencyCode,
      LastInventoryDate: payload.lastInventoryDate ? new Date(payload.lastInventoryDate).toISOString() : null,
      NextInventoryDate: payload.nextInventoryDate ? new Date(payload.nextInventoryDate).toISOString() : null,
      Status: payload.status,
      IsActive: payload.isActive,
      AllowsNegativeStock: payload.allowsNegativeStock,
      TrackBatch: payload.trackBatch,
      TrackExpiration: payload.trackExpiration,
      StockGroup: payload.stockGroup,
      Notes: payload.notes
    };
  }

  private mapPagedResponse(response: any): StockPagedResult {
    const dataContainer = response?.Data ?? response?.data ?? response?.Result ?? response?.result ?? response;

    const rawItems = Array.isArray(dataContainer?.Items)
      ? dataContainer.Items
      : Array.isArray(dataContainer?.items)
        ? dataContainer.items
        : Array.isArray(dataContainer?.Data)
          ? dataContainer.Data
          : Array.isArray(dataContainer?.data)
            ? dataContainer.data
            : Array.isArray(dataContainer?.Records)
              ? dataContainer.Records
              : Array.isArray(dataContainer?.records)
                ? dataContainer.records
                : Array.isArray(dataContainer?.List)
                  ? dataContainer.List
                  : Array.isArray(dataContainer?.list)
                    ? dataContainer.list
                    : Array.isArray(dataContainer)
                      ? dataContainer
                      : [];

    const items = this.mapListFromDto(rawItems);

    const page = response?.Page ?? response?.page ?? dataContainer?.Page ?? dataContainer?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? dataContainer?.PageSize ?? dataContainer?.pageSize ?? items.length;
    const totalItems = response?.TotalItems
      ?? response?.totalItems
      ?? response?.TotalRecords
      ?? response?.totalRecords
      ?? response?.TotalCount
      ?? response?.totalCount
      ?? dataContainer?.TotalItems
      ?? dataContainer?.totalItems
      ?? dataContainer?.TotalRecords
      ?? dataContainer?.totalRecords
      ?? dataContainer?.TotalCount
      ?? dataContainer?.totalCount
      ?? items.length;
    const totalPages = response?.TotalPages
      ?? response?.totalPages
      ?? dataContainer?.TotalPages
      ?? dataContainer?.totalPages
      ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
